import {
  Building2,
  Layers,
  LayoutDashboard,
  MessageSquare,
  Plug,
  ScrollText,
  Settings,
  Users,
} from 'lucide-react';

import type { NavSection } from '@platform/ui';

import { PLATFORM_ENDPOINT_PERMISSIONS } from './api/platform';
import { PLATFORM_PERMISSIONS, type Permissions } from './session/permissions';

/**
 * The super admin navigation (FR-SUPER, FR-UI-05, MASTER_PLAN M12.7, M2.8).
 *
 * Items are filtered by the caller's permissions from `GET /auth/session`. That is a
 * **display** decision and nothing else: every endpoint behind every item checks the same
 * key on the server, and `platform_support` reaching a link it should not see would be
 * refused there (rules §17.2, §22.2). What the filter buys is an operator who is not
 * shown a menu of things their role cannot do.
 *
 * With the permission sets in `@platform/contracts`, `platform_support` sees Overview,
 * Tenants, Users and Logs; `platform_admin` additionally sees Plans, Catalog, Settings
 * and Feedback (`feedback.moderate`, which support does not hold). A section whose every
 * item is hidden disappears with its heading, rather than leaving an empty "Platform"
 * label.
 *
 * It lives here rather than in `__root.tsx` so a test can assert the shape without
 * mounting a router.
 */
export function navigation(pathname: string, can: Permissions['can']): NavSection[] {
  const primary = [
    {
      key: 'overview',
      label: 'Overview',
      href: '/',
      active: pathname === '/',
      icon: <LayoutDashboard size={18} strokeWidth={2} />,
      visible: true,
    },
    {
      key: 'tenants',
      label: 'Tenants',
      href: '/tenants',
      active: pathname.startsWith('/tenants'),
      icon: <Building2 size={18} strokeWidth={2} />,
      visible: can(PLATFORM_PERMISSIONS.tenantRead),
    },
    {
      key: 'plans',
      label: 'Plans',
      href: '/plans',
      active: pathname.startsWith('/plans'),
      icon: <Layers size={18} strokeWidth={2} />,
      visible: can(PLATFORM_PERMISSIONS.plansManage),
    },
    {
      key: 'catalog',
      label: 'Catalog',
      href: '/catalog',
      active: pathname.startsWith('/catalog'),
      icon: <Plug size={18} strokeWidth={2} />,
      visible: can(PLATFORM_PERMISSIONS.catalogManage),
    },
  ];

  const platform = [
    {
      key: 'users',
      label: 'Users',
      href: '/users',
      active: pathname.startsWith('/users'),
      icon: <Users size={18} strokeWidth={2} />,
      visible: can(PLATFORM_PERMISSIONS.usersRead),
    },
    {
      key: 'logs',
      label: 'Logs',
      href: '/logs',
      active: pathname.startsWith('/logs'),
      icon: <ScrollText size={18} strokeWidth={2} />,
      visible: can(PLATFORM_PERMISSIONS.logsRead),
    },
    {
      key: 'feedback',
      label: 'Feedback',
      href: '/feedback',
      active: pathname.startsWith('/feedback'),
      icon: <MessageSquare size={18} strokeWidth={2} />,
      visible: can(PLATFORM_ENDPOINT_PERMISSIONS.feedbackModerate),
    },
    {
      key: 'settings',
      label: 'Settings',
      href: '/settings',
      active: pathname.startsWith('/settings'),
      icon: <Settings size={18} strokeWidth={2} />,
      visible: can(PLATFORM_PERMISSIONS.settingsManage),
    },
  ];

  const strip = ({ visible: _visible, ...item }: (typeof primary)[number]) => item;

  const sections: NavSection[] = [{ items: primary.filter((item) => item.visible).map(strip) }];
  const platformItems = platform.filter((item) => item.visible).map(strip);
  if (platformItems.length > 0) sections.push({ label: 'Platform', items: platformItems });
  return sections;
}

/**
 * The page title for a path (M13.7, WCAG 2.4.2 Page Titled).
 *
 * Same list of names the menu uses, extended with the routes the menu does not list: the
 * six tenant sub-pages, and the two screens that render outside the shell. Longest prefix
 * wins, so `/tenants/:id/users` beats `/tenants`.
 */
const TITLES: [prefix: string, title: string][] = [
  ['/tenants', 'Tenants'],
  ['/plans', 'Plans'],
  ['/catalog', 'Catalog'],
  ['/users', 'Users'],
  ['/logs', 'Logs'],
  ['/feedback', 'Feedback'],
  ['/settings', 'Settings'],
  ['/account/security', 'Your security'],
  ['/mfa/enroll', 'Set up two-factor authentication'],
  ['/login', 'Sign in'],
];

/** The tenant sub-pages, which all sit under `/tenants/:tenantId/…`. */
const TENANT_TABS: Record<string, string> = {
  users: 'Tenant users',
  features: 'Tenant features',
  branding: 'Tenant identity & branding',
  domains: 'Tenant domains',
  integrations: 'Tenant integrations',
};

/** `"Tenants · HeyAllergy Console"`. */
export function pageTitle(pathname: string): string {
  const tenantTab = /^\/tenants\/[^/]+\/([^/]+)/.exec(pathname)?.[1];
  if (tenantTab !== undefined && tenantTab in TENANT_TABS) {
    return `${TENANT_TABS[tenantTab] ?? 'Tenant'} · HeyAllergy Console`;
  }
  const match = TITLES.filter(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  )
    .sort((a, b) => b[0].length - a[0].length)
    .at(0);
  return `${match?.[1] ?? 'Overview'} · HeyAllergy Console`;
}
